/**
 * Command Center API client (demo feature).
 * Backend: adrsta-models app/api/v1/command_center.py
 */

import axiosInstance from "../../utils/axiosInstance";

export type CcMode = "ml" | "deck";

export const CC_MODES: CcMode[] = ["ml", "deck"];

export const isCcMode = (m: string | undefined): m is CcMode => m === "ml" || m === "deck";

export const ccBase = (mode: CcMode) => `/command-center/${mode}`;

// ── Engine (server-owned results) ─────────────────────────────────────────────

export interface DataFacts {
  missing_columns: string[];
  columns: string[];
  kpi_column?: string;
  markets: number;
  periods: number;
  granularity: "weekly" | "daily";
  history_weeks: number;
  incomplete_markets: number;
  flat_markets: number;
  cleaned_names: number;
  has_adspend: boolean;
  weekly_spend_k?: number | null;
  /** The last run of periods with ad spend, when it looks like a past campaign. */
  campaign_spend?: { from: string; to: string; periods: number; total: number } | null;
  date_from: string | null;
  date_to: string | null;
  rows: number;
}

export interface Candidate { name: string; key: string; share: number }
/** GeoLift's "Regression Type" (the synthetic control model). */
export type RegressionType = "SCM" | "Ridge ASCM";

export interface Design {
  /** Row index, or "plan" for the combined synthetic-control plan. */
  id: number | string;
  rank: number;
  markets: string[];
  weeks: number;
  mde: number;
  power: number;
  investment_k: number;
  weekly_investment_k: number;
  incremental: number;
  holdout: number;
  duration?: number;
  /** BestMarkets row ID, for the GeoLift "View control market" charts. */
  selection_id?: number;
}

/** RCT design from the Adrsta RCT engine (rct_experiment.runRctExperimentDesign). */
export interface RctDesign {
  split: number;
  split_key: string;
  weeks: number;
  test_markets: number;
  control_markets: number;
  markets: string[];
  control: string[];
  mde: number;
  power: number;
  investment_k: number;
  weekly_investment_k: number;
  bau_weekly_k: number;
  rmspe: number;
  chart: { dates: string[]; actual: number[]; predicted: number[] };
  /** Unrounded engine values, as the RCT Experiment's Results screen shows them. */
  exact?: Partial<Record<"incrementalInvestment" | "percentageLift" | "mde" | "standardError" | "averageBaselineKPI"
    | "selectedEffectSize" | "validationRMSPE" | "validationSD" | "validationBias" | "weeklyCost" | "normalizedAverageError", number | null>>;
}

/** Live progress of a running design / readout job. */
export interface LiveJob {
  job_id: string;
  kind: "sc_design" | "rct_design" | "sc_readout" | "rct_readout";
  phase: string | null;
  progress: number;
  message: string | null;
  started_at: string | null;
}

export type LiveSlot = "power" | "rct" | "readout";

export interface Readout {
  status: "running" | "done" | "failed";
  job_id?: string;
  method?: "sc" | "rct";
  control_markets?: number;
  file_name?: string;
  started_at?: string;
  error?: string;
  lift: number;
  incremental: number;
  pvalue: number;
  confidence: number;
  sales_with: number;
  sales_without: number;
  spend_k: number;
  planned_spend_k: number;
  spend_source: string;
  iroas: number | null;
  cost_per_incremental: number | null;
  campaign_periods: number;
  chart?: { t?: number[]; c?: number[]; start_index?: number; dates?: string[] };
  /** Mid-flight read: last date in the file, weeks of the flight it covers, and whether it covers all of it. */
  as_of?: string | null;
  weeks_in?: number;
  planned_weeks?: number | null;
  final?: boolean;
  planned_total_k?: number;
}

/** One mid-flight read (engine.reads, oldest first). */
export interface ReadSummary {
  job_id: string;
  method?: "sc" | "rct";
  as_of?: string | null;
  weeks_in?: number;
  planned_weeks?: number | null;
  final?: boolean;
  lift: number;
  confidence: number;
  pvalue?: number;
  incremental: number;
  iroas: number | null;
  cost_per_incremental: number | null;
  spend_k: number;
  planned_spend_k?: number;
  spend_source?: string;
  file_name?: string;
  finished_at?: string;
}

/** One partner results deck (engine.deck_reads, oldest first). */
export interface DeckRead {
  name: string;
  uploaded_at: string;
  final: boolean;
  lift?: number | null;
  confidence?: number | null;
  iroas?: number | null;
  incremental?: number | null;
  spend_k?: number | null;
  weeks?: number | null;
}

/** Numbers read from a partner deck (adrsta-models deck_parser.py). */
export interface DeckFields {
  method?: "Synthetic control" | "RCT";
  split?: number;
  test_markets?: string[];
  n_test?: number;
  control?: string;
  n_control?: number;
  weeks?: number;
  flight_start?: string;
  flight_end?: string;
  weekly_budget_k?: number;
  total_budget_k?: number;
  bau_weekly_k?: number;
  mde?: number;
  power?: number;
  expected_incremental?: number;
  lift?: number;
  incremental?: number;
  spend_k?: number;
  iroas?: number;
  confidence?: number;
  pvalue?: number;
  series?: { dates: string[]; t: number[]; c: number[]; start_index: number };
}

export interface DeckParse { kind: string; fields: DeckFields; missing: string[]; pages: number; error: string | null }

export interface StoredDeck { name: string; size: number; key: string; content_type: string; uploaded_at: string; parsed?: DeckParse; final?: boolean }

export interface Engine {
  sample?: boolean;
  history?: {
    status: "ready" | "failed";
    file_name: string;
    error?: string | null;
    facts: DataFacts;
    job_config_id?: string | null;
    candidates?: Candidate[];
    /** Campaign start date the suggestions were clustered for (unset = the whole history). */
    candidates_start?: string;
    names?: Record<string, string>;
  } | null;
  power?: {
    status: "running" | "done" | "failed";
    job_id?: string;
    anchors?: string[];
    /** Markets the power analysis ran on (where ads will run). */
    markets?: string[];
    /** Campaign start date in the history; the test window runs from it to the end of the file. */
    campaign_start?: string;
    weeks?: number;
    bau_monthly_k?: number | null;
    /** GeoLift's "Regression Type" the power analysis ran with. */
    regression_type?: RegressionType;
    /** The study's "Statistical Significance Threshold %" this run used (older runs: 80). */
    stat_sig_threshold?: number;
    designs: Design[];
    /** All analysed markets together: the design the test runs. */
    plan?: Design | null;
    curves: Record<string, { effect: number; power: number }[]>;
    started_at?: string;
    error?: string | null;
  } | null;
  rct?: {
    status: "running" | "done" | "failed";
    job_id?: string;
    split: number;
    bau_weekly_k: number;
    durations: number[];
    weekly_cap_k: number;
    /** Exact dollars as typed (newer runs). */
    total_weekly_spend?: number;
    weekly_cap?: number;
    started_at?: string;
    error?: string | null;
    design?: RctDesign;
    dropped?: string[];
    market_rows?: { name: string; weekly_k: number }[];
    /** Full-history KPI total per design market (test and control). */
    market_sales?: Record<string, number>;
  } | null;
  readout?: Readout | null;
  decks?: Partial<Record<"design" | "results", StoredDeck>>;
  reads?: ReadSummary[] | null;
  deck_reads?: DeckRead[] | null;
}

export interface CcTest<S = Record<string, any>> {
  id: string;
  mode: CcMode;
  name: string;
  advertiser: string;
  stage: number;
  status: string;
  state: S;
  engine: Engine;
  channel: string | null;
  flight_start: string | null;
  flight_weeks: number | null;
  created_at: string | null;
  updated_at: string | null;
  /** Running design / readout jobs, keyed by engine slot. */
  live?: Partial<Record<LiveSlot, LiveJob>>;
}

// ── Checks / rules / library / calendar ───────────────────────────────────────

export type CcLevel = "rec" | "guard" | "block";
export type CheckStage = "plan" | "data" | "design" | "results";

export interface CheckResult {
  rule_id: string;
  check: string;
  stage: CheckStage;
  label: string;
  level: CcLevel;
  status: "pass" | "fail" | "pending";
  detail: string;
}

export interface Rule {
  id: string;
  mode: "ml" | "deck" | "both";
  check: string;
  threshold: number | null;
  level: CcLevel;
  enabled: boolean;
  is_default: boolean;
  label: string;
  stage: CheckStage | null;
  description: string;
}

export interface CatalogCheck {
  key: string;
  stage: CheckStage;
  modes: CcMode[];
  label: string;
  unit: string;
  default: number | null;
  description: string;
}

export interface LibraryEntry {
  id: string;
  mode: CcMode;
  name: string;
  advertiser: string | null;
  channel: string;
  season: string;
  weeks: number;
  source: string;
  lift: number;
  roas: number | null;
  met: boolean;
  notes: string | null;
  test_id: string | null;
  is_default: boolean;
}

export interface CalendarFlight {
  test_id: string;
  advertiser: string;
  name: string;
  start: string;
  end: string;
  weeks: number;
  kind: "planning" | "scheduled" | "live" | "ended" | "done";
  stage: number;
  conflict_with: string[];
}

export interface CcCalendar {
  from: string;
  to: string;
  today: string;
  flights: CalendarFlight[];
  holidays: { name: string; date: string }[];
}

const root = "/api/v1/command-center";
type TestRes = { test: CcTest };

const upload = async (url: string, file: File | null, onProgress?: (pct: number) => void, extra?: Record<string, string>) => {
  const form = new FormData();
  if (file) form.append("file", file);
  else form.append("use_sample", "true");
  Object.entries(extra || {}).forEach(([k, v]) => form.append(k, v));
  return (await axiosInstance.post<TestRes>(url, form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: e => { if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
  })).data.test;
};

export const ccApi = {
  listTests: async (mode: CcMode) => (await axiosInstance.get<{ tests: CcTest[] }>(`${root}/${mode}/tests`)).data.tests,
  createTest: async (mode: CcMode, body: { name: string; advertiser: string; stage: number; status: string; state: object }) =>
    (await axiosInstance.post<TestRes>(`${root}/${mode}/tests`, body)).data.test,
  getTest: async (id: string) => (await axiosInstance.get<TestRes>(`${root}/tests/${id}`)).data.test,
  updateTest: async (id: string, body: { name?: string; advertiser?: string; stage?: number; status?: string; state?: object }) =>
    (await axiosInstance.patch<TestRes>(`${root}/tests/${id}`, body)).data.test,
  deleteTest: async (id: string) => { await axiosInstance.delete(`${root}/tests/${id}`); },

  // Engine (Command Center + ML)
  uploadHistory: (id: string, file: File | null, onProgress?: (p: number) => void) => upload(`${root}/tests/${id}/history`, file, onProgress),
  suggestMarkets: async (id: string, campaign_start: string) =>
    (await axiosInstance.post<TestRes>(`${root}/tests/${id}/suggested-markets`, { campaign_start })).data.test,
  startPower: async (id: string, body: { markets: string[]; campaign_start?: string; bau_monthly_k?: number; regression_type?: RegressionType; stat_sig_threshold?: number }) =>
    (await axiosInstance.post<TestRes>(`${root}/tests/${id}/power-analysis`, body)).data.test,
  pollPower: async (id: string) => (await axiosInstance.get<TestRes>(`${root}/tests/${id}/power-analysis`)).data.test,
  startRct: async (id: string, body: { split: number; durations: number[]; weekly_cap: number; total_weekly_spend: number }) =>
    (await axiosInstance.post<TestRes>(`${root}/tests/${id}/rct-design`, body)).data.test,
  startReadout: (id: string, file: File | null, onProgress?: (p: number) => void) => upload(`${root}/tests/${id}/readout`, file, onProgress),
  pollReadout: async (id: string) => (await axiosInstance.get<TestRes>(`${root}/tests/${id}/readout`)).data.test,
  checks: async (id: string, stage?: CheckStage) =>
    (await axiosInstance.get<{ checks: CheckResult[] }>(`${root}/tests/${id}/checks`, { params: stage ? { stage } : {} })).data.checks,

  /** The RCT design's Market Level Investments as .xlsx (same sheet as the RCT Experiment's Download). */
  downloadRctMarketLevel: async (id: string) => {
    const res = await axiosInstance.get(`${root}/tests/${id}/rct-design/market-level.xlsx`, { responseType: "blob" });
    const name = /filename="([^"]+)"/.exec(String(res.headers?.["content-disposition"] || ""))?.[1] || "market_level_investments_5050.xlsx";
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  },

  // Decks (no ML)
  /** final: a results deck sent after the flight ended (earlier ones are mid-flight reads). */
  uploadDeck: (id: string, kind: "design" | "results", file: File, onProgress?: (p: number) => void, final?: boolean) =>
    upload(`${root}/tests/${id}/decks/${kind}`, file, onProgress, kind === "results" ? { final: String(!!final) } : undefined),
  downloadDeck: async (id: string, kind: "design" | "results", name: string) => {
    const res = await axiosInstance.get(`${root}/tests/${id}/decks/${kind}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  },

  // Rules
  rules: async (mode: CcMode) => (await axiosInstance.get<{ rules: Rule[] }>(`${root}/${mode}/rules`)).data.rules,
  catalog: async () => (await axiosInstance.get<{ checks: CatalogCheck[] }>(`${root}/rules/catalog`)).data.checks,
  createRule: async (mode: CcMode, body: { check: string; threshold: number | null; level: CcLevel; applies_to_both: boolean }) =>
    (await axiosInstance.post<{ rule: Rule }>(`${root}/${mode}/rules`, body)).data.rule,
  updateRule: async (id: string, body: { level?: CcLevel; enabled?: boolean; threshold?: number }) =>
    (await axiosInstance.patch<{ rule: Rule }>(`${root}/rules/${id}`, body)).data.rule,
  deleteRule: async (id: string) => { await axiosInstance.delete(`${root}/rules/${id}`); },

  // Library
  library: async (mode: CcMode) => (await axiosInstance.get<{ entries: LibraryEntry[] }>(`${root}/${mode}/library`)).data.entries,
  addLibrary: async (mode: CcMode, body: Omit<LibraryEntry, "id" | "mode" | "test_id" | "is_default">) =>
    (await axiosInstance.post<{ entry: LibraryEntry }>(`${root}/${mode}/library`, body)).data.entry,
  deleteLibrary: async (id: string) => { await axiosInstance.delete(`${root}/library/${id}`); },

  // Calendar
  calendar: async (mode: CcMode) => (await axiosInstance.get<CcCalendar>(`${root}/${mode}/calendar`)).data,
};
